import * as THREE from 'three';
import type { Environment } from './Environment';
import { clamp, mulberry32, smoothstep } from './noise';

const TRAIL_SAMPLES = 14;
const TRAIL_SUBDIV = 3;
const TRAIL_LIFE = 0.14;
const TRAIL_VERTS = ((TRAIL_SAMPLES - 1) * TRAIL_SUBDIV + 1) * 2;

type Sample = { base: THREE.Vector3; tip: THREE.Vector3; time: number; power: number };

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();

function catmull(p0: THREE.Vector3, p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3, t: number, out: THREE.Vector3): THREE.Vector3 {
  const t2 = t * t;
  const t3 = t2 * t;
  return out.set(
    0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
    0.5 * (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
    0.5 * (2 * p1.z + (-p0.z + p2.z) * t + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3),
  );
}

/** Thin additive ribbon swept by the outer blade over the last ~0.14 s. */
class BladeTrail {
  readonly mesh: THREE.Mesh;
  private readonly samples: Sample[] = [];
  private readonly positions = new Float32Array(TRAIL_VERTS * 3);
  private readonly params = new Float32Array(TRAIL_VERTS * 2);
  private readonly geometry = new THREE.BufferGeometry();

  constructor(env: Environment) {
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    this.geometry.setAttribute('aTrail', new THREE.BufferAttribute(this.params, 2).setUsage(THREE.DynamicDrawUsage));
    const index: number[] = [];
    for (let i = 0; i < TRAIL_VERTS / 2 - 1; i++) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    this.geometry.setIndex(index);
    this.mesh = new THREE.Mesh(
      this.geometry,
      new THREE.ShaderMaterial({
        uniforms: { uSunColor: env.uniforms.uSunColor },
        vertexShader: /* glsl */ `
          attribute vec2 aTrail;
          varying vec2 vTrail;
          void main() {
            vTrail = aTrail;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          uniform vec3 uSunColor;
          varying vec2 vTrail;
          void main() {
            // x: remaining life * power, y: 0 at the inner blade, 1 at the tip.
            float life = vTrail.x;
            float edge = smoothstep(0.55, 1.0, vTrail.y);
            float body = smoothstep(0.0, 0.9, vTrail.y) * 0.4;
            float a = life * (body + edge * 1.2);
            vec3 col = mix(vec3(1.0, 0.93, 0.8), uSunColor, 0.35) * (1.1 + edge * 1.1);
            gl_FragColor = vec4(col * a, 1.0);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    this.mesh.frustumCulled = false;
    this.mesh.name = 'slash-trail';
    this.mesh.renderOrder = 2;
    this.mesh.visible = false;
  }

  update(time: number, base: THREE.Vector3, tip: THREE.Vector3, power: number): void {
    if (power > 0.02) {
      const s = this.samples.length >= TRAIL_SAMPLES ? this.samples.shift()! : { base: new THREE.Vector3(), tip: new THREE.Vector3(), time: 0, power: 0 };
      s.base.copy(base);
      s.tip.copy(tip);
      s.time = time;
      s.power = power;
      this.samples.push(s);
    }
    while (this.samples.length && time - this.samples[0].time > TRAIL_LIFE) this.samples.shift();
    const n = this.samples.length;
    this.mesh.visible = n >= 2;
    if (n < 2) return;
    let v = 0;
    for (let i = 0; i < n - 1; i++) {
      const s0 = this.samples[Math.max(i - 1, 0)];
      const s1 = this.samples[i];
      const s2 = this.samples[i + 1];
      const s3 = this.samples[Math.min(i + 2, n - 1)];
      const steps = i === n - 2 ? TRAIL_SUBDIV + 1 : TRAIL_SUBDIV;
      for (let k = 0; k < steps; k++) {
        const t = k / TRAIL_SUBDIV;
        catmull(s0.base, s1.base, s2.base, s3.base, t, _a);
        catmull(s0.tip, s1.tip, s2.tip, s3.tip, t, _b);
        const age = time - (s1.time + (s2.time - s1.time) * t);
        const life = clamp(1 - age / TRAIL_LIFE, 0, 1) * (s1.power + (s2.power - s1.power) * t);
        this.positions.set([_a.x, _a.y, _a.z, _b.x, _b.y, _b.z], v * 3);
        this.params.set([life, 0, life, 1], v * 2);
        v += 2;
      }
    }
    this.geometry.setDrawRange(0, Math.max(0, (v / 2 - 1) * 6));
    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.aTrail.needsUpdate = true;
  }
}

const DEBRIS = 180;

/** Pooled cut stalks and seed fluff that spin away from the blade and settle in the wind. */
class Debris {
  readonly points: THREE.Points;
  private readonly pos = new Float32Array(DEBRIS * 3);
  private readonly vel = new Float32Array(DEBRIS * 3);
  private readonly life = new Float32Array(DEBRIS);
  private readonly maxLife = new Float32Array(DEBRIS);
  private readonly spin = new Float32Array(DEBRIS);
  private readonly attr = new Float32Array(DEBRIS * 4);
  private next = 0;
  private readonly rng = mulberry32(31);
  private readonly uniforms: Record<string, THREE.IUniform>;

  constructor(env: Environment) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('aDebris', new THREE.BufferAttribute(this.attr, 4).setUsage(THREE.DynamicDrawUsage));
    this.uniforms = { uSunColor: env.uniforms.uSunColor, uSunDir: env.uniforms.uSunDir, uPixelScale: { value: 400 } };
    this.points = new THREE.Points(
      geometry,
      new THREE.ShaderMaterial({
        uniforms: this.uniforms,
        vertexShader: /* glsl */ `
          uniform vec3 uSunDir;
          uniform float uPixelScale;
          attribute vec4 aDebris; // alpha, size, angle, kind
          varying vec4 vDebris;
          varying float vGlow;
          void main() {
            vDebris = aDebris;
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            vec3 viewDir = normalize(position - cameraPosition);
            vGlow = 0.5 + pow(max(dot(viewDir, uSunDir), 0.0), 4.0) * 2.0;
            gl_Position = projectionMatrix * mv;
            gl_PointSize = aDebris.x > 0.0 ? clamp(uPixelScale * aDebris.y / -mv.z, 2.0, 26.0) : 0.0;
          }
        `,
        fragmentShader: /* glsl */ `
          uniform vec3 uSunColor;
          varying vec4 vDebris;
          varying float vGlow;
          void main() {
            vec2 c = gl_PointCoord - 0.5;
            float s = sin(vDebris.z);
            float k = cos(vDebris.z);
            c = vec2(c.x * k - c.y * s, c.x * s + c.y * k);
            // Stalk: a thin lit sliver. Fluff: a soft dot.
            float stalk = smoothstep(0.08, 0.0, abs(c.x)) * smoothstep(0.5, 0.3, abs(c.y));
            float fluff = smoothstep(0.5, 0.0, length(c));
            float a = mix(stalk, fluff * fluff, vDebris.w) * vDebris.x;
            vec3 col = uSunColor * mix(vec3(0.95, 0.72, 0.38), vec3(1.0, 0.9, 0.72), vDebris.w) * 0.55 * vGlow;
            gl_FragColor = vec4(col * a, 1.0);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.points.frustumCulled = false;
    this.points.name = 'slash-debris';
  }

  spawn(at: THREE.Vector3, push: THREE.Vector3, count: number): void {
    const r = this.rng;
    for (let n = 0; n < count; n++) {
      const i = this.next;
      this.next = (this.next + 1) % DEBRIS;
      this.pos.set([at.x + (r() - 0.5) * 0.12, at.y + (r() - 0.5) * 0.1, at.z + (r() - 0.5) * 0.12], i * 3);
      const kick = 0.25 + r() * 0.35;
      this.vel.set([push.x * kick + (r() - 0.5) * 1.2, 0.8 + r() * 1.8, push.z * kick + (r() - 0.5) * 1.2], i * 3);
      this.maxLife[i] = 0.8 + r() * 0.9;
      this.life[i] = this.maxLife[i];
      this.spin[i] = (r() - 0.5) * 18;
      const fluff = r() < 0.3 ? 1 : 0;
      this.attr.set([1, fluff ? 0.05 + r() * 0.03 : 0.07 + r() * 0.06, r() * 6.28, fluff], i * 4);
    }
  }

  update(dt: number, wind: THREE.Vector3, ground: (x: number, z: number) => number, viewportHeight: number): void {
    this.uniforms.uPixelScale.value = viewportHeight;
    const drag = Math.exp(-dt * 2.2);
    for (let i = 0; i < DEBRIS; i++) {
      if (this.life[i] <= 0) {
        this.attr[i * 4] = 0;
        continue;
      }
      this.life[i] -= dt;
      const j = i * 3;
      const fluff = this.attr[i * 4 + 3];
      this.vel[j] = wind.x + (this.vel[j] - wind.x) * drag;
      this.vel[j + 2] = wind.z + (this.vel[j + 2] - wind.z) * drag;
      this.vel[j + 1] = this.vel[j + 1] * drag - (fluff ? 1.2 : 5.5) * dt;
      this.pos[j] += this.vel[j] * dt;
      this.pos[j + 1] += this.vel[j + 1] * dt;
      this.pos[j + 2] += this.vel[j + 2] * dt;
      const g = ground(this.pos[j], this.pos[j + 2]) + 0.05;
      if (this.pos[j + 1] < g) {
        this.pos[j + 1] = g;
        this.vel[j + 1] = 0;
      }
      this.attr[i * 4 + 2] += this.spin[i] * dt;
      const t = this.life[i] / this.maxLife[i];
      this.attr[i * 4] = smoothstep(0, 0.35, t) * smoothstep(1, 0.9, t);
    }
    const geometry = this.points.geometry;
    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.aDebris.needsUpdate = true;
  }
}

/** Flat ring of wind that bursts outward under the feet on the double jump. */
class WindRing {
  readonly mesh: THREE.Mesh;
  private readonly uniforms = { uAge: { value: 1 }, uStrength: { value: 0 }, uSunColor: { value: new THREE.Color() } };
  private age = 1;
  private duration = 0.5;
  private radius = 2.4;

  constructor(env: Environment) {
    this.uniforms.uSunColor = env.uniforms.uSunColor;
    const geometry = new THREE.PlaneGeometry(2, 2, 1, 1);
    geometry.rotateX(-Math.PI / 2);
    this.mesh = new THREE.Mesh(
      geometry,
      new THREE.ShaderMaterial({
        uniforms: this.uniforms,
        vertexShader: /* glsl */ `
          varying vec2 vP;
          void main() {
            vP = position.xz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          uniform float uAge;
          uniform float uStrength;
          uniform vec3 uSunColor;
          varying vec2 vP;
          void main() {
            float r = length(vP);
            float front = mix(0.18, 0.92, 1.0 - pow(1.0 - uAge, 2.2));
            float ring = exp(-pow((r - front) / (0.05 + 0.1 * uAge), 2.0));
            float inner = exp(-pow((r - front * 0.7) / 0.08, 2.0)) * 0.35;
            // Streaks along the radius so it reads as moving air rather than a solid disc.
            float ang = atan(vP.y, vP.x);
            float streak = 0.55 + 0.45 * sin(ang * 23.0 + r * 9.0) * sin(ang * 7.0 - uAge * 6.0);
            float a = (ring + inner) * streak * (1.0 - uAge) * uStrength * step(r, 1.0);
            gl_FragColor = vec4(mix(vec3(1.0, 0.95, 0.85), uSunColor, 0.3) * a * 0.8, 1.0);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    this.mesh.frustumCulled = false;
    this.mesh.name = 'wind-ring';
    this.mesh.visible = false;
  }

  burst(at: THREE.Vector3, radius: number, strength: number, duration: number): void {
    this.mesh.position.copy(at);
    this.radius = radius;
    this.mesh.scale.setScalar(radius);
    this.uniforms.uStrength.value = strength;
    this.duration = duration;
    this.age = 0;
    this.mesh.visible = true;
  }

  update(dt: number): void {
    if (!this.mesh.visible) return;
    this.age += dt / this.duration;
    this.uniforms.uAge.value = Math.min(this.age, 1);
    this.mesh.scale.setScalar(this.radius);
    if (this.age >= 1) this.mesh.visible = false;
  }
}

/** Sword and double-jump effects: one trail mesh, one debris point cloud, one ring. */
export class SlashFx {
  readonly group = new THREE.Group();
  private readonly trail: BladeTrail;
  private readonly debris: Debris;
  private readonly ring: WindRing;

  constructor(env: Environment) {
    this.trail = new BladeTrail(env);
    this.debris = new Debris(env);
    this.ring = new WindRing(env);
    this.group.add(this.trail.mesh, this.debris.points, this.ring.mesh);
  }

  /** `power` 0..1 drives the trail brightness (blade speed). */
  updateTrail(time: number, base: THREE.Vector3, tip: THREE.Vector3, power: number): void {
    this.trail.update(time, base, tip, power);
  }

  spawnDebris(at: THREE.Vector3, push: THREE.Vector3, count: number): void {
    this.debris.spawn(at, push, count);
  }

  windRing(at: THREE.Vector3, radius: number, strength: number, duration: number): void {
    this.ring.burst(at, radius, strength, duration);
  }

  update(dt: number, wind: THREE.Vector3, ground: (x: number, z: number) => number, viewportHeight: number): void {
    this.debris.update(dt, wind, ground, viewportHeight);
    this.ring.update(dt);
  }
}
